import * as React from "react";
import Debug from "debug";
import {
  Box,
  Container,
  Avatar,
  Typography,
  Grid,
  Button,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { CheckboxWithLabel, TextField } from "formik-material-ui";
import {
  LockOutlined as LockOutlinedIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { useProvidedAuth, useProvidedTranslations } from "contexts";
import { useAPI, useJSONSchema } from "hooks";
import Loading from "components/generic/Loading";
import InputSelect from "components/generic/Input/FormSelect";
import { isValidNIF } from "lib";

const debug = Debug("app:comp:register");

const cfg = {
  formSchema: {
    type: "object",
    required: [
      "firstName",
      "surname1",
      "dni",
      "phone",
      "email",
      "password",
      "repeated-password",
      "acceptRGPD",
      "address",
      "postcode",
      "town",
      "province",
    ],
    additionalProperties: false,
    properties: {
      firstName: { type: "string", minLength: 1 },
      surname1: { type: "string", minLength: 1 },
      surname2: { type: "string" },
      phone: { type: "string", pattern: "[0-9]{8}" },
      dni: { type: "string" },
      email: { type: "string", format: "email" },
      password: { type: "string", minLength: 6 },
      "repeated-password": { type: "string", minLength: 6 },
      acceptRGPD: { type: "boolean", enum: [true] },
      address: { type: "string", minLength: 1 },
      postcode: { type: "string", minLength: 5 },
      town: { type: "string", minLength: 1 },
      province: { type: "string", minLength: 1 },
    },
  },
  extraValidation(formData) {
    if (formData.password !== formData["repeated-password"]) {
      return [
        {
          keyword: "must-match-password",
          dataPath: "repeated-password",
          message: "Must match with password",
        },
      ];
    }
    if (!isValidNIF(formData.dni)){
      return [
        {
          keyword: "nif-format-incorrect",
          dataPath: "dni",
          message: "Incorrect DNI",
        },
      ];
    }

    return [];
  },
  initialValues: {
    firstName: "",
    surname1: "",
    surname2: "",
    dni: "",
    phone: "",
    email: "",
    password: "",
    "repeated-password": "",
    acceptRGPD: false,
    address: "",
    postcode: "",
    town: "",
    province: "",
  },
};

const validProvinces = ["coruña", "lugo", "orense", "pontevedra"];

const GuestRegisterProvinceInput = (props) => {
  const formatMessage = useProvidedTranslations();
  const options = validProvinces.map((el) => ({
    value: el,
    label: formatMessage(`app.province._${el}`),
  }));
  return <InputSelect {...props} options={options} />;
};

const personalDataInputs = [
  { id: "firstName", type: "text", grid: { xs: 12 }, component: TextField },
  {
    id: "surname1",
    type: "text",
    grid: { xs: 12, sm: 6 },
    component: TextField,
  },
  {
    id: "surname2",
    type: "text",
    grid: { xs: 12, sm: 6 },
    component: TextField,
  },
  { id: "phone", type: "text", grid: { xs: 12, sm: 6 }, component: TextField },
  { id: "dni", type: "text", grid: { xs: 12, sm: 6 }, component: TextField },
  {
    id: "address",
    autoComplete: "street-address",
    type: "text",
    grid: { xs: 12, sm: 8 },
    component: TextField,
  },
  {
    id: "postcode",
    autoComplete: "postal-code",
    type: "text",
    grid: { xs: 12, sm: 4 },
    component: TextField,
  },
  {
    id: "town",
    autoComplete: "address-level-2",
    type: "text",
    grid: { xs: 12, sm: 6 },
    component: TextField,
  },
  {
    id: "province",
    autoComplete: "address-level-1",
    type: "text",
    grid: { xs: 12, sm: 6 },
    component: GuestRegisterProvinceInput,
  },
];

const accessDataInputs = [
  {
    id: "email",
    autoComplete: "email",
    type: "email",
    grid: { xs: 12 },
    component: TextField,
  },
  {
    id: "password",
    autoComplete: "actual-password",
    type: "password",
    grid: { xs: 12, sm: 6 },
    component: TextField,
  },
  {
    id: "repeated-password",
    autoComplete: "new-password",
    type: "password",
    grid: { xs: 12, sm: 6 },
    component: TextField,
  },
];

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  section: {
    fontWeight: "bold",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const GuestRegisterForm = function ({ isSubmitting, isFetching }) {
  const formatMessage = useProvidedTranslations();
  const classes = useStyles();

  return (
    <Form className={classes.form}>
      <Grid container spacing={2}>
        <Typography className={classes.section} component="h2" variant="h6">
          {formatMessage("app.register.form.personal-section")}
        </Typography>
        {personalDataInputs.map((formInput) => (
          <Grid item {...formInput.grid} key={formInput.id}>
            <Field
              component={formInput.component}
              name={formInput.id}
              id={formInput.id}
              type={formInput.type}
              label={formatMessage(`app.register.form.${formInput.id}.label`)}
              fullWidth
              variant="outlined"
              autoComplete={formInput.autoComplete}
            />
          </Grid>
        ))}
        <Typography className={classes.section} component="h2" variant="h6">
          {formatMessage("app.register.form.access-section")}
        </Typography>
        {accessDataInputs.map((formInput) => (
          <Grid item {...formInput.grid} key={formInput.id}>
            <Field
              component={formInput.component}
              name={formInput.id}
              id={formInput.id}
              type={formInput.type}
              label={formatMessage(`app.register.form.${formInput.id}.label`)}
              fullWidth
              variant="outlined"
              autoComplete={formInput.autoComplete}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Field
            component={CheckboxWithLabel}
            type="checkbox"
            name="acceptRGPD"
            color="primary"
            Label={{
              label: (
                <p>
                  {formatMessage("app.register.form.rgpd.label")}
                  <RouterLink target="_blank" to="/terms-and-conditions">
                    {formatMessage("app.register.form.rgpd.label.link")}
                  </RouterLink>
                </p>
              ),
            }}
          />
          <ErrorMessage name="acceptRGPD">
            {(message) => <FormHelperText error>{message}</FormHelperText>}
          </ErrorMessage>
        </Grid>
      </Grid>
      <Button
        type="submit"
        fullWidth
        disabled={isSubmitting || isFetching}
        variant="contained"
        color="primary"
        className={classes.submit}
      >
        {formatMessage("app.register.form.submit")}
      </Button>
    </Form>
  );
};

const GuestRegisterError = (props) => {
  const { error } = props;
  const formatMessage = useProvidedTranslations();
  switch (error.status) {
    case 400:
      return (
        <Box width={1}>
          <Alert severity="error" variant="filled">
            {formatMessage("app.register.error.already-existing-email")}
          </Alert>
        </Box>
      );

    default:
      return (
        <Box width={1}>
          <Alert severity="error" variant="filled">
            {error.toString()}
          </Alert>
        </Box>
      );
  }
};

const GuestRegister = () => {
  const formatMessage = useProvidedTranslations();
  const classes = useStyles();
  const [, dispatchToken] = useProvidedAuth();
  const validate = useJSONSchema(cfg.formSchema, cfg.extraValidation);
  const handleSuccess = React.useCallback(
    (resp) => {
      debug("HANDLE_SUCCESS");
      dispatchToken({ type: "NEW_TOKEN", payload: resp.token });
    },
    [dispatchToken]
  );
  const [{ isLoading, isFetching, error }, doFetch] = useAPI(
    {
      wait: true,
      endpoint: "login/user",
      method: "POST",
    },
    handleSuccess
  );

  const handleSubmit = async (formData) => {
    // TODO: add a cb into useAPI doFetch
    debug("formData is", formData);
    doFetch({ body: { ...formData, lang: "es" } });
  };

  return (
    <Container component="main" maxWidth="sm">
      <div className={classes.paper}>
        <Box position="absolute" left="0">
          <Button component={RouterLink} to="/">
            <Avatar className={classes.avatar}>
              <ArrowBackIcon />
            </Avatar>
          </Button>
        </Box>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {formatMessage("app.register.title")}
        </Typography>
        {error && <GuestRegisterError error={error} />}
        {isLoading ? (
          <Loading />
        ) : (
          <Formik
            initialValues={cfg.initialValues}
            onSubmit={handleSubmit}
            validate={validate}
            component={GuestRegisterForm}
            isFetching={isFetching}
          />
        )}
      </div>
    </Container>
  );
};

export default GuestRegister;
